import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { mapToVoid } from '@examdojo/rxjs';
import { TableInsertModel, TableUpdateModel } from '@examdojo/supabase';
import { sanitizeObject } from '@examdojo/util/sanitize-object';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AssessmentTL2RelationshipHttpService } from './assessment-tl2-relationship-http.service';
import {
  ASSESSMENT_TL2_RELATIONSHIP_CREATE_ALLOWED_KEYS,
  ASSESSMENT_TL2_RELATIONSHIP_UPDATE_ALLOWED_KEYS,
  AssessmentTL2RelationshipCreateModel,
  AssessmentTL2RelationshipHttpModel,
  AssessmentTL2RelationshipStoreModel,
  AssessmentTL2RelationshipUpdateModel,
} from './assessment-tl2-relationship.model';
import { AssessmentTL2RelationshipStore } from './assessment-tl2-relationship.store';

@Injectable({
  providedIn: 'root',
})
export class AssessmentTl2RelationshipService {
  constructor(
    private readonly store: AssessmentTL2RelationshipStore,
    private readonly httpService: AssessmentTL2RelationshipHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  fetchAll(): Observable<AssessmentTL2RelationshipStoreModel[]> {
    return this.httpService.fetchAll().pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments' }),
      map((data) => data.map((item) => this.mapHttpModelToStoreModel(item))),
      tap((storeModels) => this.store.set(storeModels)),
    );
  }

  setMany(
    assessmentId: AssessmentTL2RelationshipStoreModel['assessment_id'],
    topicLevel2Ids: Array<AssessmentTL2RelationshipStoreModel['topic_level_02_id']>,
  ) {
    return this.httpService.set(assessmentId, topicLevel2Ids).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'update' }),
      switchMap(() => this.fetchAll()),
      mapToVoid(),
    );
  }

  delete(id: AssessmentTL2RelationshipStoreModel['id']) {
    return this.httpService.delete(id).pipe(
      tap(() => {
        this.store.delete(id);
      }),
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.assessments', action: 'delete' }),
    );
  }

  private mapCreateModelToHttpUpdateModel(
    model: AssessmentTL2RelationshipCreateModel,
  ): TableInsertModel<'assessments_topics_level_02', 'learning'> {
    return sanitizeObject(model, ASSESSMENT_TL2_RELATIONSHIP_CREATE_ALLOWED_KEYS);
  }

  private mapUpdateModelToHttpUpdateModel(
    model: AssessmentTL2RelationshipUpdateModel,
  ): TableUpdateModel<'assessments_topics_level_02', 'learning'> {
    return sanitizeObject(model, ASSESSMENT_TL2_RELATIONSHIP_UPDATE_ALLOWED_KEYS);
  }

  private mapHttpModelToStoreModel(httpModel: AssessmentTL2RelationshipHttpModel): AssessmentTL2RelationshipStoreModel {
    return { ...httpModel, id: `${httpModel.assessment_id}-${httpModel.topic_level_02_id}` };
  }
}
